import React, { useState, useEffect } from 'react';
import { Modal, Card, Button, Space, Typography, Tag, List, Row, Col, message } from 'antd';
import { CheckOutlined, RocketOutlined, CrownOutlined, StarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { API_BASE as apiBase } from '../../config/api';

const { Title, Text } = Typography;

const UpgradeModal = ({ visible, onClose, limitType, currentPlan }) => {
  const [plans, setPlans] = useState([]);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    business_email: '',
    phone: '',
    website: '',
    company_size: '',
    industry: '',
    message: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (visible) {
      const fetchPlans = async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = { 'Content-Type': 'application/json' };
          if (token) headers.Authorization = `Bearer ${token}`;
          
          const res = await fetch(`${apiBase}/public/subscription-plans`, { headers });
          if (res.ok) {
            const data = await res.json();
            const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
            const allowedPlans = ['starter', 'growth', 'enterprise', 'white label'];
            const filtered = items.filter(p => {
              if (!p.is_active || !p.plan_name) return false;
              const lowerName = p.plan_name.toLowerCase();
              return allowedPlans.some(allowed => lowerName.includes(allowed));
            });
            // Remove duplicates by plan name (keep first occurrence)
            const uniquePlans = [];
            const seenNames = new Set();
            for (const plan of filtered) {
              const lowerName = plan.plan_name.toLowerCase();
              if (!seenNames.has(lowerName)) {
                seenNames.add(lowerName);
                uniquePlans.push(plan);
              }
            }
            setPlans(uniquePlans);
          }
        } catch (err) {
          console.error('Error fetching plans:', err);
        }
      };
      fetchPlans();
    }
  }, [visible]);

  const getLimitMessage = () => {
    const messages = {
      websites: 'You\'ve reached your website limit',
      events: 'You\'ve reached your monthly event limit',
      users: 'You\'ve reached your team member limit',
      data_retention: 'You need longer data retention'
    };
    return messages[limitType] || 'Upgrade to unlock more features';
  };

  const getPlanIcon = (planName) => {
    if (planName.toLowerCase().includes('enterprise')) return <CrownOutlined />;
    if (planName.toLowerCase().includes('growth')) return <RocketOutlined />;
    if (planName.toLowerCase().includes('white')) return <StarOutlined />;
    return <CheckOutlined />;
  };

  const getPlanColor = (planName) => {
    if (planName.toLowerCase().includes('enterprise')) return '#722ed1';
    if (planName.toLowerCase().includes('growth')) return '#1890ff';
    if (planName.toLowerCase().includes('white')) return '#000';
    return '#52c41a';
  };

  const formatFeature = (feature) => {
    if (typeof feature === 'string') return feature;
    if (typeof feature === 'object' && feature !== null) {
      return JSON.stringify(feature);
    }
    return String(feature);
  };

  const getFeaturesList = (features) => {
    if (!features) return [];
    
    const list = [];
    if (features.websites) list.push(`${features.websites} website${features.websites > 1 ? 's' : ''}`);
    if (features.events_per_month) list.push(`${features.events_per_month.toLocaleString()} events/month`);
    if (features.users) list.push(`${features.users} user${features.users > 1 ? 's' : ''}`);
    if (features.data_retention_days) list.push(`${features.data_retention_days} days data retention`);
    if (features.data_retention_months) list.push(`${features.data_retention_months} months data retention`);
    if (features.heatmaps) list.push(`Heatmaps: ${formatFeature(features.heatmaps)}`);
    if (features.analytics) list.push(`Analytics: ${formatFeature(features.analytics)}`);
    if (features.api_key_access) list.push('API Access');
    if (features.ai_agent) list.push('AI Agent');
    
    return list;
  };

  const handleSelectPlan = async (plan) => {
    // For White Label plans, show contact sales modal
    if (plan.plan_name.toLowerCase().includes('white')) {
      setShowContactModal(true);
      return;
    }

    if (!plan?.stripe_price_id) {
      onClose();
      navigate('/dashboard/admin/subscription', { state: { selectedPlan: plan.id } });
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      message.error('Please log in to upgrade.');
      return;
    }

    try {
      setLoadingPlanId(plan.id);
      // Fetch user profile for checkout payload
      const meRes = await fetch(`${apiBase}/me`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const meBody = await meRes.json().catch(() => ({}));
      if (!meRes.ok || !meBody?.id || !meBody?.email) {
        throw new Error('Unable to load your account details. Please refresh.');
      }

      const checkoutRes = await fetch(`${apiBase}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId: meBody.id,
          email: meBody.email,
          priceId: plan.stripe_price_id,
          planId: plan.id,
        })
      });
      const checkoutBody = await checkoutRes.json().catch(() => ({}));
      if (!checkoutRes.ok || !checkoutBody?.url) {
        throw new Error(checkoutBody?.error || 'Failed to start checkout');
      }
      window.location.href = checkoutBody.url;
    } catch (err) {
      message.error(err?.message || 'Failed to start checkout');
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.company_name || !contactForm.business_email) {
      setContactError('Please provide your company name and business email.');
      return;
    }
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);
    try {
      const rawWebsite = (contactForm.website || '').trim();
      const normalizedWebsite = rawWebsite
        ? `https://${rawWebsite.replace(/^https?:\/\//i, '')}`
        : '';
      const res = await fetch(`${apiBase}/sales/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          website: normalizedWebsite,
          plan: 'White Label',
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && data.error) || 'Failed to send enquiry');
      }
      setContactSuccess(true);
      setContactForm({
        company_name: '',
        contact_name: '',
        business_email: '',
        phone: '',
        website: '',
        company_size: '',
        industry: '',
        message: '',
      });
    } catch (err) {
      setContactError(err.message || 'Failed to send enquiry');
    } finally {
      setContactSubmitting(false);
    }
  };

  const closeContactModal = () => {
    if (contactSubmitting) return;
    setShowContactModal(false);
  };

  return (
    <Modal
      title={
        <Space>
          <RocketOutlined style={{ color: '#1890ff' }} />
          <span>Upgrade Your Plan</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      style={{ top: 20 }}
    >
      <div style={{ marginBottom: 24 }}>
        <Alert
          message={getLimitMessage()}
          description="Choose a plan that fits your needs and unlock more capacity."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {currentPlan && (
          <Text type="secondary">
            Current Plan: <Tag color="blue">{currentPlan}</Tag>
          </Text>
        )}
      </div>

      <Row gutter={[16, 16]} justify="center">
        {plans.map((plan) => {
          const features = plan.features || {};
          const featuresList = getFeaturesList(features);
          const isCurrentPlan = currentPlan && plan.plan_name === currentPlan;
          
          return (
            <Col xs={24} sm={12} lg={6} key={plan.id}>
              <Card
                hoverable={!isCurrentPlan}
                style={{
                  height: '100%',
                  border: isCurrentPlan ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  opacity: isCurrentPlan ? 0.7 : 1
                }}
                bodyStyle={{ padding: 16 }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      {getPlanIcon(plan.plan_name)}
                      <Title level={4} style={{ margin: 0, color: getPlanColor(plan.plan_name) }}>
                        {plan.plan_name}
                      </Title>
                    </div>
                    {isCurrentPlan && (
                      <Tag color="blue">Current Plan</Tag>
                    )}
                  </div>

                  <div>
                    <Text style={{ fontSize: 32, fontWeight: 'bold', color: getPlanColor(plan.plan_name) }}>
                      £{plan.monthly_price || 0}
                    </Text>
                    <Text type="secondary">/month</Text>
                    {plan.yearly_price && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          or £{plan.yearly_price}/year
                        </Text>
                      </div>
                    )}
                  </div>

                  <List
                    size="small"
                    dataSource={featuresList}
                    renderItem={item => (
                      <List.Item style={{ padding: '4px 0', border: 'none' }}>
                        <Space size="small">
                          <CheckOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                          <Text style={{ fontSize: 12 }}>{item}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />

                  <Button
                    type={isCurrentPlan ? 'default' : 'primary'}
                    block
                    disabled={isCurrentPlan}
                    loading={loadingPlanId === plan.id}
                    onClick={() => handleSelectPlan(plan)}
                    style={{
                      background: !isCurrentPlan ? getPlanColor(plan.plan_name) : undefined,
                      borderColor: !isCurrentPlan ? getPlanColor(plan.plan_name) : undefined
                    }}
                  >
                    {isCurrentPlan ? 'Current Plan' : plan.plan_name.toLowerCase().includes('white') ? 'Contact Sales' : 'Select Plan'}
                  </Button>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          All plans include email support. Upgrade or downgrade anytime.
        </Text>
      </div>

      {/* Contact Sales Modal */}
      {showContactModal && (
        <Modal
          title={
            <Space>
              <StarOutlined style={{ color: '#000' }} />
              <span>White Label - Talk to Sales</span>
            </Space>
          }
          open={showContactModal}
          onCancel={closeContactModal}
          footer={null}
          width={600}
          style={{ top: 20 }}
        >
          <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>
            Share a few details about your business and we will reach out with White Label pricing and rollout options.
          </p>
          <form onSubmit={handleContactSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Company name*
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={contactForm.company_name}
                  onChange={handleContactChange}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Contact name
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={contactForm.contact_name}
                  onChange={handleContactChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Business email*
                </label>
                <input
                  type="email"
                  name="business_email"
                  value={contactForm.business_email}
                  onChange={handleContactChange}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={contactForm.phone}
                  onChange={handleContactChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Website
                </label>
                <input
                  type="text"
                  name="website"
                  value={contactForm.website}
                  onChange={handleContactChange}
                  placeholder="yourdomain.com"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Company size
                </label>
                <select
                  name="company_size"
                  value={contactForm.company_size}
                  onChange={handleContactChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                >
                  <option value="">Select company size</option>
                  <option value="1-10">1-10</option>
                  <option value="11-50">11-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201-500">201-500</option>
                  <option value="501-1000">501-1000</option>
                  <option value="1000+">1000+</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Industry
                </label>
                <select
                  name="industry"
                  value={contactForm.industry}
                  onChange={handleContactChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14 
                  }}
                >
                  <option value="">Select industry</option>
                  <option value="SaaS / Software">SaaS / Software</option>
                  <option value="E-commerce / Retail">E-commerce / Retail</option>
                  <option value="Agency / Services">Agency / Services</option>
                  <option value="Marketplace / Platform">Marketplace / Platform</option>
                  <option value="Fintech">Fintech</option>
                  <option value="Education">Education</option>
                  <option value="Media / Publishing">Media / Publishing</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 14, marginBottom: 4, fontWeight: 500 }}>
                  Anything else we should know?
                </label>
                <textarea
                  name="message"
                  rows={4}
                  value={contactForm.message}
                  onChange={handleContactChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d9d9d9', 
                    fontSize: 14,
                    resize: 'vertical' 
                  }}
                />
              </div>
            </div>
            {contactError && (
              <p style={{ marginTop: 16, fontSize: 13, color: '#ff4d4f' }}>{contactError}</p>
            )}
            {contactSuccess && (
              <p style={{ marginTop: 16, fontSize: 13, color: '#52c41a' }}>
                Thank you. Your request has been sent to our sales team.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <Button
                type="default"
                onClick={closeContactModal}
                disabled={contactSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={contactSubmitting}
              >
                {contactSubmitting ? 'Sending…' : 'Send to sales'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Modal>
  );
};

// Missing Alert import - add it
const Alert = ({ message, description, type, showIcon, style }) => {
  const colors = {
    info: { bg: '#e6f7ff', border: '#91d5ff', color: '#0050b3' },
    warning: { bg: '#fffbe6', border: '#ffe58f', color: '#ad6800' },
    error: { bg: '#fff2f0', border: '#ffccc7', color: '#cf1322' },
    success: { bg: '#f6ffed', border: '#b7eb8f', color: '#389e0d' }
  };
  
  const color = colors[type] || colors.info;
  
  return (
    <div style={{
      padding: 16,
      background: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: 8,
      ...style
    }}>
      <div style={{ fontWeight: 600, color: color.color, marginBottom: 4 }}>{message}</div>
      {description && <div style={{ fontSize: 14, color: color.color }}>{description}</div>}
    </div>
  );
};

export default UpgradeModal;
